import { SYSTEM_HEALTH_COPY } from '../constants/system-health.copy';
import { BannerState } from '../types/banner-state.enum';
import { BannerView, ComponentView } from '../types/system-health.types';

import { isIssue, isUnavailable } from './is-issue';

export interface BannerInput {
  components: ComponentView[];
  hasLoaded: boolean;
  isOffline: boolean;
  hasAuthError: boolean;
  isDataDelayed: boolean;
}

/**
 * Major = at least a quarter of the monitored dependencies are down (never fewer
 * than two) — or every single one of a small set: "1 of 1 down" is not partial.
 */
const MAJOR_SHARE = 0.25;
const MAJOR_MIN_COUNT = 2;

/**
 * Resolves the page-wide banner from the rows and the connection state.
 *
 * @param input - Rows plus the load, offline and auth flags.
 * @returns Banner state and its copy.
 */
export function buildBanner({
  components,
  hasLoaded,
  isOffline,
  hasAuthError,
  isDataDelayed,
}: BannerInput): BannerView {
  const state = resolveBannerState(components, hasLoaded, isOffline, hasAuthError, isDataDelayed);
  return { state, ...SYSTEM_HEALTH_COPY.banner[state] };
}

function resolveBannerState(
  components: ComponentView[],
  hasLoaded: boolean,
  isOffline: boolean,
  hasAuthError: boolean,
  isDataDelayed: boolean
): BannerState {
  if (hasAuthError) return BannerState.AuthError;
  if (isOffline) return BannerState.Offline;
  if (!hasLoaded) return BannerState.Loading;
  if (components.length === 0) return BannerState.Empty;

  const unavailable = components.filter(isUnavailable).length;
  const unknown = components.filter(component => isIssue(component) && !isUnavailable(component)).length;

  if (unavailable === 0) return unknown > 0 || isDataDelayed ? BannerState.Delayed : BannerState.Available;
  const isAllDown = unavailable === components.length;
  const isMajor = isAllDown || (unavailable >= MAJOR_MIN_COUNT && unavailable / components.length >= MAJOR_SHARE);
  return isMajor ? BannerState.Major : BannerState.Partial;
}
